import { MAX_NODE_KEY_LENGTH, NODE_KEY_PATTERN } from '../documents/template.js';
import { DomainError } from '../errors.js';

/**
 * Yetki ve onay matrisinde ISM formunun konusu (subject) (ADR 0042 §8, §9; ADR 0028 #38 madde 3'ü
 * değiştirir) [sorular #38, #233]. İki biçim vardır:
 * - `folder:<node_key>`: kategori. Kalıcı düğüm anahtarı bütün gemilerde aynı kategoriyi gösterir
 *   (ADR 0037 §3, ADR 0038 §4); klasör UUID'si gemi başına kopyadır, konu olamaz (ADR 0038 §2).
 * - `document:<uuid>`: tek form (şirket belgesi `documents.id` ya da gemi belgesi
 *   `vessel_documents.id`).
 * Başka biçim `INVALID_SUBJECT`. Bu modül saftır.
 */

export const SUBJECT_KINDS = ['folder', 'document'] as const;
export type SubjectKind = (typeof SUBJECT_KINDS)[number];

export type ParsedSubject =
  | { kind: 'folder'; nodeKey: string }
  | { kind: 'document'; documentId: string };

const FOLDER_PREFIX = 'folder:';
const DOCUMENT_PREFIX = 'document:';
/** Küçük harfli kanonik UUID: aynı konu iki yazılışla iki ayrı hücre olamaz. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Konuyu çözümler; biçim dışı değer `INVALID_SUBJECT` (sorular #233). */
export function parseSubject(value: string): ParsedSubject {
  if (typeof value === 'string') {
    if (value.startsWith(FOLDER_PREFIX)) {
      const nodeKey = value.slice(FOLDER_PREFIX.length);
      // ADR 0038 §4 anahtar biçimi: bölümle başlar, en çok 6 parça, en çok 120 karakter
      if (nodeKey.length <= MAX_NODE_KEY_LENGTH && NODE_KEY_PATTERN.test(nodeKey)) {
        return { kind: 'folder', nodeKey };
      }
    } else if (value.startsWith(DOCUMENT_PREFIX)) {
      const documentId = value.slice(DOCUMENT_PREFIX.length);
      if (UUID_PATTERN.test(documentId)) return { kind: 'document', documentId };
    }
  }
  throw new DomainError('INVALID_SUBJECT', String(value));
}

/** Kategori konusu `folder:<node_key>`. */
export function folderSubject(nodeKey: string): string {
  const subject = `${FOLDER_PREFIX}${nodeKey}`;
  parseSubject(subject);
  return subject;
}

/** Tek form konusu `document:<uuid>`. */
export function documentSubject(documentId: string): string {
  const subject = `${DOCUMENT_PREFIX}${documentId}`;
  parseSubject(subject);
  return subject;
}

export interface SubjectChainInput {
  /** Formun belge kimliği; yoksa zincir yalnız klasörlerden oluşur. */
  documentId?: string | null;
  /**
   * Belgenin klasöründen köke doğru (yakından uzağa) klasör anahtarları. Anahtarsız klasör
   * (`null`; sorular #165) zincire girmez.
   */
  folderKeys: readonly (string | null)[];
}

/**
 * Konu zinciri (ADR 0042 §8): önce belge, sonra klasör ve atalarının anahtarları, yakından uzağa;
 * tekrar eden konu bir kez. Zincir taslak açılırken gemi sunucusunda bir kez hesaplanır ve
 * `form_records.subjects`'e yazılır; hücre ve onay kuralı seçimi bu sırayla yapılır.
 */
export function subjectChain(input: SubjectChainInput): string[] {
  const chain: string[] = [];
  if (input.documentId) chain.push(documentSubject(input.documentId));
  for (const key of input.folderKeys) {
    if (key === null) continue;
    const subject = folderSubject(key);
    if (!chain.includes(subject)) chain.push(subject);
  }
  return chain;
}
